(() => {
  'use strict';

  if (!('PerformanceObserver' in window) || !window.posthog) return;

  const script = document.currentScript;
  const pageTemplate = sanitizeToken(script?.dataset?.pageTemplate, 'unknown');
  const viewportCategory = window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop';
  const maxShiftEntries = 8;
  const maxSourcesPerShift = 3;
  const observers = [];

  let active = false;
  let sent = false;
  let startedAfterConsentChange = false;
  let collectorEverStarted = false;
  let collectorStartedAt = 0;
  let collectionBoundary = 0;
  let firstHiddenAt = Infinity;
  let clsValue = 0;
  let cumulativeShiftValue = 0;
  let sessionValue = 0;
  let sessionStartTime = 0;
  let sessionLastEntryTime = 0;
  let largestShiftValue = 0;
  let largestShiftTarget = '';
  let shiftCount = 0;
  let shiftEntries = [];
  let lcpValue = null;
  let lcpTarget = '';

  function sanitizeToken(value, fallback = '') {
    if (typeof value !== 'string') return fallback;
    const normalized = value.toLowerCase().slice(0, 48);
    if (!/^[a-z][a-z0-9_-]*$/.test(normalized)) return fallback;
    if (/\d{4,}/.test(normalized)) return fallback;
    if (/(?:account|address|customer|email|first.?name|last.?name|phone|user)/.test(normalized)) return fallback;
    return normalized;
  }

  function describeNode(node) {
    if (!node || !node.tagName) return '';

    const tag = sanitizeToken(String(node.tagName), 'element');
    const role = sanitizeToken(node.getAttribute?.('role'));
    const roleSuffix = role ? `[role=${role}]` : '';

    return tag.concat(roleSuffix).slice(0, 80);
  }

  function analyticsAllowed() {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true;
    } catch (_error) {
      return false;
    }
  }

  function entryAllowed(entry) {
    return entry.startTime >= collectionBoundary;
  }

  // A page rendered in a background tab does not paint until it is foregrounded,
  // so its LCP entry carries the time the visitor finally looked at the tab, not
  // a load speed. Discard LCP at or after the first hidden moment; layout shifts
  // are unaffected because a hidden page does not render.
  function lcpEntryAllowed(entry) {
    return entryAllowed(entry) && entry.startTime < firstHiddenAt;
  }

  function observe(type, callback) {
    try {
      const consume = (entries) => callback(entries);
      const observer = new PerformanceObserver((list) => consume(list.getEntries()));
      observer.observe({ type, buffered: true });
      observers.push({ observer, consume });
    } catch (_error) {
      // Older browsers can expose PerformanceObserver without this entry type.
    }
  }

  function disconnectObservers() {
    observers.splice(0).forEach(({ observer }) => observer.disconnect?.());
  }

  function drainObserverRecords() {
    observers.forEach(({ observer, consume }) => {
      const records = observer.takeRecords?.() || [];
      if (records.length) consume(records);
    });
  }

  function resetMeasurements() {
    clsValue = 0;
    cumulativeShiftValue = 0;
    sessionValue = 0;
    sessionStartTime = 0;
    sessionLastEntryTime = 0;
    largestShiftValue = 0;
    largestShiftTarget = '';
    shiftCount = 0;
    shiftEntries = [];
    lcpValue = null;
    lcpTarget = '';
  }

  function stopAndDiscard() {
    disconnectObservers();
    active = false;
    resetMeasurements();
  }

  function restartAfterLifecycle() {
    if (!analyticsAllowed() || active) return;
    disconnectObservers();
    sent = false;
    resetMeasurements();
    start();
  }

  function start() {
    if (active || sent || !analyticsAllowed()) return;
    active = true;
    collectorStartedAt = Math.round(performance.now?.() || 0);
    // Buffered observers replay historical entries: only the first consented
    // start may accept them; any restart or post-consent start must reject
    // entries from before its own boundary, or prior-lifecycle metrics repeat.
    if (collectorEverStarted || startedAfterConsentChange) collectionBoundary = collectorStartedAt;
    collectorEverStarted = true;
    // An ordinary lifecycle that begins hidden never has a trustworthy LCP, so
    // bound it at zero. Prerendered pages begin hidden by design and remain
    // eligible for the valid LCP produced after activation.
    firstHiddenAt = document.visibilityState === 'hidden' && !document.prerendering ? 0 : Infinity;

    observe('largest-contentful-paint', (entries) => {
      if (!active) return;
      const eligibleEntries = entries.filter(lcpEntryAllowed);
      const entry = eligibleEntries[eligibleEntries.length - 1];
      if (!entry) return;
      lcpValue = entry.startTime;
      lcpTarget = describeNode(entry.element);
    });

    observe('layout-shift', (entries) => {
      if (!active) return;

      entries.forEach((entry) => {
        if (!entryAllowed(entry) || entry.hadRecentInput) return;

        cumulativeShiftValue += entry.value;
        shiftCount += 1;

        if (
          sessionValue > 0 &&
          entry.startTime - sessionLastEntryTime < 1000 &&
          entry.startTime - sessionStartTime < 5000
        ) {
          sessionValue += entry.value;
        } else {
          sessionValue = entry.value;
          sessionStartTime = entry.startTime;
        }
        sessionLastEntryTime = entry.startTime;

        if (sessionValue > clsValue) clsValue = sessionValue;

        const sources = Array.from(entry.sources || [])
          .slice(0, maxSourcesPerShift)
          .map((source) => describeNode(source.node))
          .filter(Boolean);

        if (entry.value > largestShiftValue) {
          largestShiftValue = entry.value;
          largestShiftTarget = sources[0] || '';
        }

        shiftEntries.push({
          start_time_ms: Math.round(entry.startTime),
          value: Number(entry.value.toFixed(4)),
          cumulative_value: Number(cumulativeShiftValue.toFixed(4)),
          session_value: Number(sessionValue.toFixed(4)),
          sources,
        });
        shiftEntries.sort((left, right) => right.value - left.value);
        if (shiftEntries.length > maxShiftEntries) shiftEntries.length = maxShiftEntries;
      });
    });
  }

  function lifecycleContext(lifecycleEvent) {
    const navigation = performance.getEntriesByType('navigation')[0];
    return {
      page_template: pageTemplate,
      viewport_category: viewportCategory,
      navigation_type: navigation?.type || 'unknown',
      lifecycle_event: lifecycleEvent,
      visibility_state: document.visibilityState,
      collector_start_ms: collectorStartedAt,
      page_age_ms: Math.round(performance.now?.() || 0),
      started_after_consent_change: startedAfterConsentChange,
      // Distinguishes "no LCP because the tab was backgrounded" from "no LCP fired".
      hidden_before_lcp: firstHiddenAt !== Infinity && lcpValue === null,
    };
  }

  function capture(payload) {
    if (typeof window.posthog?.capture !== 'function') return false;
    try {
      window.posthog.capture('$web_vital', payload);
      return true;
    } catch (_error) {
      return false;
    }
  }

  function flush(lifecycleEvent) {
    if (sent || !active) return;
    drainObserverRecords();
    if (!analyticsAllowed() || typeof window.posthog?.capture !== 'function') {
      stopAndDiscard();
      return;
    }

    sent = true;
    const common = lifecycleContext(lifecycleEvent);

    if (lcpValue !== null) {
      const lcpCaptured = capture({
        ...common,
        metric_name: 'LCP',
        value: Math.round(lcpValue),
        target: lcpTarget,
      });
      if (!lcpCaptured) {
        disconnectObservers();
        active = false;
        return;
      }
    }

    capture({
      ...common,
      metric_name: 'CLS',
      value: Number(clsValue.toFixed(4)),
      cumulative_shift_value: Number(cumulativeShiftValue.toFixed(4)),
      largest_shift_value: Number(largestShiftValue.toFixed(4)),
      largest_shift_target: largestShiftTarget,
      shift_count: shiftCount,
      shifts: shiftEntries,
      evidence_status: 'unresolved_field_cls',
    });
    disconnectObservers();
    active = false;
  }

  document.addEventListener('visitorConsentCollected', () => {
    if (analyticsAllowed()) {
      startedAfterConsentChange = true;
      start();
    } else {
      stopAndDiscard();
    }
  });

  document.addEventListener('DOMContentLoaded', start, { once: true });
  document.addEventListener('visibilitychange', (event) => {
    if (document.visibilityState === 'hidden') {
      // Recorded before flushing so entries drained from the buffer are judged
      // against the exact high-resolution moment the page actually went away.
      const hiddenAt = Number.isFinite(event.timeStamp) ? event.timeStamp : performance.now?.() || 0;
      firstHiddenAt = Math.min(firstHiddenAt, hiddenAt);
      flush('visibility_hidden');
    } else if (document.visibilityState === 'visible') {
      restartAfterLifecycle();
    }
  });
  window.addEventListener('pagehide', () => flush('pagehide'));
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    restartAfterLifecycle();
  });

  start();
})();
